// The invoice-selection criteria, and the one vocabulary two screens share
// (owner, 2026-09-14).
//
// Analytics → Reporting has asked these sixteen questions since Exported Date
// joined them (D-440); fifteen before that, since it shipped.
// Analytics → Dashboard now asks the SAME sixteen, through the Filters button
// in the screen header — the owner's instruction was to abandon the dashboard's
// own four narrow controls "in favor of the ones from the Reporting screen", and
// the only way two screens can genuinely offer one filter set is to hold it in
// one place. A second copy would drift on the first change to either.
//
// WHAT LIVES HERE: the form's shape, its empty value, the Status and Flags
// option lists, and the three conversions — to `ReportCriteria` for
// `POST /reports`, to `AnalyticsParams` for the aggregates, and to
// `InvoiceListParams` for the listing under them.
//
// WHAT DOES NOT: anything about how a control is drawn. That is
// `ReportingScreen.tsx`'s form and `DashboardFilterButton.tsx`'s popover, which
// present the same questions in the space each has.

import type { AnalyticsParams, InvoiceListParams, ReportCriteria } from '../api/types'
import type { MultiSelectOption } from '../components/MultiSelectField'
import { FLAG_TYPES, TIMELINE_NODES } from './constants'

/**
 * The Status options: the PIPELINE's in-progress nodes, then the terminal
 * statuses INDIVIDUALLY (owner, 2026-09-10).
 *
 * `TIMELINE_NODES`' labels are what the user sees across the top of the Invoices
 * tab, so those are the ones they can answer a question about, and each expands
 * to the stored statuses behind it. `Pending Matching` is deliberately absent —
 * it is not a pipeline node, it is the seconds an invoice spends being read.
 *
 * BUT ARCHIVES IS NOT ONE OPTION, and collapsing it was a real defect (owner:
 * *"What if I want to filter for an invoice in archives that was either approved
 * or denied?"*). The Archives node stands for eight terminal statuses spanning
 * two opposite outcomes — two ways of reaching Point Click Care, and six ways
 * of being archived unpaid — so one tick made exactly that question unaskable.
 * The eight are offered separately, under their own heading, and their option
 * values ARE the stored statuses.
 */
const ARCHIVE_NODE = TIMELINE_NODES.find((node) => node.archive === true)

export const STATUS_OPTIONS: MultiSelectOption[] = [
  ...TIMELINE_NODES.filter((node) => node.archive !== true).map((node) => ({
    value: node.key as string,
    label: node.label,
    group: 'In progress',
  })),
  // The stored status is the label as well as the value: these strings are
  // already plain English, and inventing display names for them would be
  // inventing identifiers (`CLAUDE.md` — exact identifiers, never abbreviated).
  ...(ARCHIVE_NODE?.queueStatuses ?? []).map((status) => ({
    value: status as string,
    label: status as string,
    group: 'Archived',
  })),
]

/** Node key -> the stored statuses it stands for. Archive statuses are NOT in
    here: they are their own options and stand for themselves. */
const STATUSES_FOR_NODE = new Map<string, readonly string[]>(
  TIMELINE_NODES.filter((node) => node.archive !== true).map((node) => [
    node.key as string,
    node.queueStatuses as readonly string[],
  ]),
)

/** How many STORED statuses the Status control can reach, all options ticked. */
export const STATUS_STORED_COUNT = new Set(
  STATUS_OPTIONS.flatMap((option) => STATUSES_FOR_NODE.get(option.value) ?? [option.value]),
).size

export const FLAG_OPTIONS: MultiSelectOption[] = Object.entries(FLAG_TYPES)
  .map(([value, flag]) => ({ value, label: flag.label }))
  .sort((a, b) => a.label.localeCompare(b.label))

/** On Hold's two options, a multi-select (owner, 2026-10-14: checking both
    means Any, so there is no Any option). Neither or both does not narrow. */
export const HELD_OPTIONS: MultiSelectOption[] = [
  { value: 'held', label: 'Held' },
  { value: 'not-held', label: 'Not Held' },
]

/**
 * The form's own state: strings and string arrays, exactly what the controls
 * hold. It is deliberately NOT `ReportCriteria` — a half-typed money field is a
 * string that is not yet a decimal, and an unticked multi-select is `[]` rather
 * than an absent key.
 */
export interface Criteria {
  companies: string[]
  vendors: string[]
  uploadedStart: string
  uploadedEnd: string
  /** When Export was clicked at Export Control (D-440). */
  exportedStart: string
  exportedEnd: string
  invoiceStart: string
  invoiceEnd: string
  totalStart: string
  totalEnd: string
  /** Account ids — the trailing half (`docs/decisions.md` D-85). */
  accounts: string[]
  /** Department ids — the leading half. */
  departments: string[]
  /** Whole codes — the two halves together. */
  codes: string[]
  /** Node keys and archive statuses; `toReportCriteria` expands them. */
  statuses: string[]
  /** `held`, `not-held`, both or neither; only one of them narrows. */
  held: string[]
  totalFlags: string[]
  unresolvedFlags: string[]
}

export const emptyCriteria: Criteria = {
  companies: [],
  vendors: [],
  uploadedStart: '',
  uploadedEnd: '',
  exportedStart: '',
  exportedEnd: '',
  invoiceStart: '',
  invoiceEnd: '',
  totalStart: '',
  totalEnd: '',
  accounts: [],
  departments: [],
  codes: [],
  statuses: [],
  held: [],
  totalFlags: [],
  unresolvedFlags: [],
}

/** How many of the sixteen questions have been answered — the number the
    Filters button shows, so a narrowed screen never looks unnarrowed. */
export function criteriaCount(form: Criteria): number {
  return [
    form.companies.length > 0,
    form.vendors.length > 0,
    form.uploadedStart !== '' || form.uploadedEnd !== '',
    form.exportedStart !== '' || form.exportedEnd !== '',
    form.invoiceStart !== '' || form.invoiceEnd !== '',
    form.totalStart.trim() !== '' || form.totalEnd.trim() !== '',
    form.accounts.length > 0,
    form.departments.length > 0,
    form.codes.length > 0,
    form.statuses.length > 0,
    form.held.length === 1,
    form.totalFlags.length > 0,
    form.unresolvedFlags.length > 0,
  ].filter(Boolean).length
}

/**
 * The Status control's option values for one pipeline NODE (owner, 2026-09-15 —
 * clicking a bar applies it as a filter).
 *
 * A pipeline node is its own option and stands for itself. **`archives` is
 * not**: the Status control deliberately offers the eight terminal statuses
 * separately, so that a reader can ask for an approved archive without also
 * getting a denied one, and there is no single `archives` option to tick. A
 * click on the Archives bar therefore selects all eight, which is what the bar
 * was counting.
 *
 * Returning `[]` for a key that is neither would silently drop the click, so an
 * unknown key falls through as itself and fails loudly at the route instead.
 */
export function statusSelectionForNode(nodeKey: string): string[] {
  if (STATUSES_FOR_NODE.has(nodeKey)) return [nodeKey]
  const archiveNode = TIMELINE_NODES.find((node) => node.key === nodeKey && node.archive === true)
  if (archiveNode !== undefined) return archiveNode.queueStatuses.map((status) => status as string)
  return [nodeKey]
}

/**
 * Add these values to a multi-select criterion, or remove them if every one is
 * already there.
 *
 * TOGGLE, because a bar click is the only way to undo a bar click without
 * opening the Filters popover: clicking the bar you just clicked puts the
 * screen back. Partial overlap ADDS the rest rather than clearing — a click
 * should never remove a value the user did not click.
 */
export function toggleValues(current: readonly string[], values: readonly string[]): string[] {
  if (values.length === 0) return [...current]
  const has = new Set(current)
  const allPresent = values.every((value) => has.has(value))
  if (allPresent) return current.filter((value) => !values.includes(value))
  return [...new Set([...current, ...values])]
}

export function toReportCriteria(form: Criteria): ReportCriteria {
  const criteria: ReportCriteria = {}
  if (form.companies.length > 0) criteria.company_id = form.companies
  if (form.vendors.length > 0) criteria.vendor_id = form.vendors
  if (form.uploadedStart) criteria.uploaded_from = form.uploadedStart
  if (form.uploadedEnd) criteria.uploaded_to = form.uploadedEnd
  if (form.exportedStart) criteria.exported_from = form.exportedStart
  if (form.exportedEnd) criteria.exported_to = form.exportedEnd
  if (form.invoiceStart) criteria.date_from = form.invoiceStart
  if (form.invoiceEnd) criteria.date_to = form.invoiceEnd
  // MONEY IS A DECIMAL STRING ON THE WIRE. The route REFUSES a number rather
  // than coercing it (`docs/api.md` §0.1), so the field's text goes over as
  // text — trimmed, never parsed to a JS number here.
  if (form.totalStart.trim()) criteria.total_min = form.totalStart.trim()
  if (form.totalEnd.trim()) criteria.total_max = form.totalEnd.trim()

  // Accounts and Departments are the two halves and Codes is the whole code
  // they make (`docs/decisions.md` D-85); the stored names match the display
  // words.
  if (form.accounts.length > 0) criteria.account_id = form.accounts
  if (form.departments.length > 0) criteria.department_id = form.departments
  if (form.codes.length > 0) criteria.code_id = form.codes

  if (form.statuses.length > 0) {
    // A node key expands to the stored statuses behind it; an archive option IS
    // a stored status and stands for itself. Either way the contract only ever
    // sees stored statuses — it knows nothing about node keys.
    const expanded = form.statuses.flatMap((value) => STATUSES_FOR_NODE.get(value) ?? [value])
    if (expanded.length > 0) {
      criteria.status = [...new Set(expanded)] as NonNullable<ReportCriteria['status']>
    }
  }
  if (form.held.length === 1) criteria.has_active_hold = form.held[0] === 'held'
  if (form.totalFlags.length > 0) criteria.flag_type_any = form.totalFlags
  if (form.unresolvedFlags.length > 0) criteria.flag_type = form.unresolvedFlags
  return criteria
}

/**
 * The same criteria as analytics query parameters.
 *
 * A straight pass-through, and it can be: the analytics routes take the §0.5
 * parameter names, and `ReportCriteria`'s keys ARE those names. Writing the
 * fields out rather than spreading is what keeps that claim checkable — a key
 * added to `ReportCriteria` that the aggregates do not accept fails to compile
 * here instead of 400ing at runtime.
 */
export function toAnalyticsParams(form: Criteria): AnalyticsParams {
  const criteria = toReportCriteria(form)
  return {
    ...(criteria.company_id === undefined ? {} : { company_id: criteria.company_id }),
    ...(criteria.vendor_id === undefined ? {} : { vendor_id: criteria.vendor_id }),
    ...(criteria.status === undefined ? {} : { status: criteria.status }),
    ...(criteria.date_from === undefined ? {} : { date_from: criteria.date_from }),
    ...(criteria.date_to === undefined ? {} : { date_to: criteria.date_to }),
    ...(criteria.uploaded_from === undefined ? {} : { uploaded_from: criteria.uploaded_from }),
    ...(criteria.uploaded_to === undefined ? {} : { uploaded_to: criteria.uploaded_to }),
    ...(criteria.exported_from === undefined ? {} : { exported_from: criteria.exported_from }),
    ...(criteria.exported_to === undefined ? {} : { exported_to: criteria.exported_to }),
    ...(criteria.total_min === undefined ? {} : { total_min: criteria.total_min }),
    ...(criteria.total_max === undefined ? {} : { total_max: criteria.total_max }),
    ...(criteria.code_id === undefined ? {} : { code_id: criteria.code_id }),
    ...(criteria.department_id === undefined
      ? {}
      : { department_id: criteria.department_id }),
    ...(criteria.account_id === undefined
      ? {}
      : { account_id: criteria.account_id }),
    ...(criteria.flag_type === undefined ? {} : { flag_type: criteria.flag_type }),
    ...(criteria.flag_type_any === undefined ? {} : { flag_type_any: criteria.flag_type_any }),
    ...(criteria.has_active_hold === undefined
      ? {}
      : { has_active_hold: criteria.has_active_hold }),
  }
}

/**
 * The same criteria as invoice-list query parameters.
 *
 * The one conversion: `InvoiceListParams` carries the two money bounds as
 * NUMBERS, because `listInvoices` formats them back to decimal strings itself
 * (`api/http.ts`, `toMoneyString`). A non-numeric half-typed value is dropped
 * rather than sent as `NaN`.
 */
export function toInvoiceListParams(form: Criteria): InvoiceListParams {
  const criteria = toReportCriteria(form)
  const money = (value: string | undefined): number | undefined => {
    if (value === undefined) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return {
    ...(criteria.company_id === undefined ? {} : { company_id: criteria.company_id }),
    ...(criteria.vendor_id === undefined ? {} : { vendor_id: criteria.vendor_id }),
    ...(criteria.status === undefined ? {} : { status: criteria.status }),
    ...(criteria.date_from === undefined ? {} : { date_from: criteria.date_from }),
    ...(criteria.date_to === undefined ? {} : { date_to: criteria.date_to }),
    ...(criteria.uploaded_from === undefined ? {} : { uploaded_from: criteria.uploaded_from }),
    ...(criteria.uploaded_to === undefined ? {} : { uploaded_to: criteria.uploaded_to }),
    ...(criteria.exported_from === undefined ? {} : { exported_from: criteria.exported_from }),
    ...(criteria.exported_to === undefined ? {} : { exported_to: criteria.exported_to }),
    ...(money(criteria.total_min) === undefined ? {} : { total_min: money(criteria.total_min) }),
    ...(money(criteria.total_max) === undefined ? {} : { total_max: money(criteria.total_max) }),
    ...(criteria.code_id === undefined ? {} : { code_id: criteria.code_id }),
    ...(criteria.department_id === undefined
      ? {}
      : { department_id: criteria.department_id }),
    ...(criteria.account_id === undefined
      ? {}
      : { account_id: criteria.account_id }),
    ...(criteria.flag_type === undefined ? {} : { flag_type: criteria.flag_type }),
    ...(criteria.flag_type_any === undefined ? {} : { flag_type_any: criteria.flag_type_any }),
    ...(criteria.has_active_hold === undefined
      ? {}
      : { has_active_hold: criteria.has_active_hold }),
  }
}
